import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUploadHistory } from "@/portals/patient/context/UploadHistoryContext";
import { toastSuccess, toastError } from "@/shared/lib/toast";
import { syncEpicData } from "../api/epicApi";
import { describeEpicError } from "../lib/epicErrorMessages";

// Handles the browser landing back on the dashboard after the real Epic OAuth round
// trip. The backend's /auth/epic/callback (router.py) redirects here with either
// ?epic_connected=1 or ?epic_error=<CODE> — never the richer epic_status/organization/
// reason shape a docs draft assumed, so that's what this reads.
//
// On success, this now sends the patient straight to the Document Panel to watch the
// sync happen live — the same "commit to the panel right away" flow
// UploadRecords.tsx's startDocumentUpload uses for a real file upload — instead of
// awaiting the whole sync here and toasting a final summary while stuck on the
// dashboard. addZipUpload/updateZipEntry below add a "sync" entry (see
// ZipUploadEntry.kind) that UploadHistoryContext's existing polling/merge logic then
// drives exactly like any upload entry: progress bar, then the Parsed Data tab.
export function useEpicConnectReturn() {
  const navigate = useNavigate();
  const { addZipUpload, updateZipEntry } = useUploadHistory();
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("epic_connected");
    const errorCode = params.get("epic_error");
    if (!connected && !errorCode) return;

    handledRef.current = true;
    // Strip immediately so a refresh (or the async work below failing) never replays
    // the same status — the backend only ever sends this once, right after Epic redirects.
    window.history.replaceState({}, "", window.location.pathname);

    if (errorCode) {
      toastError(describeEpicError(errorCode));
      return;
    }

    // A local id, same role as UploadRecords.tsx's `staged.id` — a stable
    // React key/selection identity from the moment this is added, updated
    // in place (not replaced) once the real sync_job_id comes back.
    const localId = crypto.randomUUID();
    addZipUpload({
      id: localId,
      fileName: "Epic Sync",
      uploadedAt: Date.now(),
      status: "processing",
      processingStage: "processing",
      processingProgress: 0,
      files: [],
      parsingIssues: [],
      kind: "sync",
      provider: "epic",
      // Without this, UploadDetailPanel defaults to the "Issues" tab (see
      // its own preferredTab ?? "issues" fallback) — empty and useless for a
      // sync entry, which has no zip/file issues to show. "structured" is
      // the Parsed Data tab's real id (see DATA_TABS), the one actually
      // streaming in records as the sync progresses — landing there
      // directly is the whole point of redirecting here in the first place.
      preferredTab: "structured",
    });
    toastSuccess("Connected to Epic. Syncing your records now…");
    navigate("/patient/documents?uploadId=" + localId);

    syncEpicData()
      .then((res) => {
        // Only uploadId changes — id stays localId forever (the same
        // "optimistic entry keeps its client-generated id" rule
        // UploadRecords.tsx's startDocumentUpload follows), since that's
        // what's both the React key/selection identity AND what the
        // ?uploadId= URL param navigated to above actually matches against
        // (UploadHistoryView selects by entry.id, not entry.uploadId).
        // Status polling starts on its own via UploadHistoryContext's
        // dispatcher effect, now that uploadId is set on a "processing"
        // "sync" entry — no separate pollSyncJobStatus() call needed.
        updateZipEntry(localId, { uploadId: res.sync_job_id });
      })
      .catch((err: Error) => {
        updateZipEntry(localId, {
          status: "failed",
          errorMessage: err.message || "Connected to Epic, but syncing your records failed.",
        });
      });
  }, [navigate, addZipUpload, updateZipEntry]);
}
